import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AdminHeader from '../../components/admin/AdminHeader';
import ImageUpload from '../../components/admin/ImageUpload';
import { supabase } from '../../integrations/supabase/client';

const { FiPackage, FiLayout, FiImage, FiPlus, FiTrash, FiEdit, FiSave, FiX, FiCheck, FiCopy, FiLoader, FiMessageSquare } = FiIcons;

const WebsiteCustomizer = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [pageContent, setPageContent] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Page Content State
  const [selectedPage, setSelectedPage] = useState('home');
  
  // UPDATED: Added distribution and common pages
  const pages = [
    { id: 'home', name: 'Home Page' },
    { id: 'about', name: 'About Page' },
    { id: 'products', name: 'Products Page' },
    { id: 'distribution', name: 'Distribution Page' },
    { id: 'contact', name: 'Contact Page' },
    { id: 'common', name: 'Common (Footer, Sustainability)' }
  ];

  // Edit State
  const [editingItem, setEditingItem] = useState(null); 
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [modalType, setModalType] = useState('product'); 
  const [filterCategory, setFilterCategory] = useState('All Products');

  useEffect(() => {
    checkUser();
    fetchData();
  }, [activeTab, selectedPage]);

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) navigate('/admin');
  };

  const fetchData = async () => {
    setLoading(true);
    if (activeTab === 'products') await fetchProducts();
    else if (activeTab === 'testimonials') await fetchTestimonials();
    else if (activeTab === 'content') await fetchPageContent(selectedPage);
    else if (activeTab === 'images') await fetchMediaFiles();
    setLoading(false);
  };

  const fetchProducts = async () => {
    const { data } = await supabase.from('products').select('*').order('created_at', { ascending: false });
    if (data) setProducts(data);
  };

  const fetchTestimonials = async () => {
    const { data } = await supabase.from('testimonials').select('*').order('created_at', { ascending: false });
    if (data) setTestimonials(data);
  };

  const fetchPageContent = async (page) => {
    const { data } = await supabase.from('page_content').select('*').eq('page', page).order('section', { ascending: true });
    if (data) setPageContent(data);
  };

  const fetchMediaFiles = async () => {
    const { data } = await supabase.storage.from('media').list();
    if (data) {
      const filesWithUrl = data.map(file => {
        const { data: urlData } = supabase.storage.from('media').getPublicUrl(file.name);
        return { ...file, publicUrl: urlData.publicUrl };
      });
      setMediaFiles(filesWithUrl);
    }
  };

  // --- Generic Save Handler ---
  const handleSaveItem = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const table = modalType === 'product' ? 'products' : 'testimonials';
    
    if (editingItem.id) {
      const { error } = await supabase.from(table).update(editingItem).eq('id', editingItem.id);
      if (!error) {
        setIsEditModalOpen(false);
        modalType === 'product' ? fetchProducts() : fetchTestimonials();
      } else alert("Error saving: " + error.message);
    } else {
      const { error } = await supabase.from(table).insert([editingItem]);
      if (!error) {
        setIsEditModalOpen(false);
        modalType === 'product' ? fetchProducts() : fetchTestimonials();
      } else alert("Error creating: " + error.message);
    }
    setSaving(false);
  };

  const handleDeleteItem = async (id, type) => {
    if (confirm("Are you sure?")) {
      const table = type === 'product' ? 'products' : 'testimonials';
      await supabase.from(table).delete().eq('id', id);
      type === 'product' ? fetchProducts() : fetchTestimonials();
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    if (item) {
      setEditingItem({ ...item });
    } else {
      // Defaults
      if (type === 'product') {
        setEditingItem({
          name: "", description: "", arabic_description: "", price: 0,
          image_url: "", category: "Medium Roast", status: "Draft",
          roast_level: "Medium", size: "250g"
        });
      } else {
        setEditingItem({
          name_en: "", name_ar: "", location_en: "", location_ar: "",
          text_en: "", text_ar: "", rating: 5, avatar_url: ""
        });
      }
    }
    setIsEditModalOpen(true);
  };

  // --- Content Logic ---
  const handleContentChange = (id, field, value) => {
    setPageContent(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const saveContent = async () => {
    setSaving(true);
    for (const item of pageContent) {
      await supabase.from('page_content').update({
        content_en: item.content_en,
        content_ar: item.content_ar,
        updated_at: new Date()
      }).eq('id', item.id);
    }
    setSaving(false);
    alert('Content updated successfully!');
  };

  // --- Categories Logic ---
  const { filteredProducts, categories } = useMemo(() => {
    const uniqueCategories = new Set(['All Products']);
    const categoryCounts = { 'All Products': products.length };
    products.forEach(p => {
      if (p.category) {
        uniqueCategories.add(p.category);
        categoryCounts[p.category] = (categoryCounts[p.category] || 0) + 1;
      }
    });
    const categoryList = Array.from(uniqueCategories).map(name => ({
      name, count: categoryCounts[name], active: name === filterCategory
    }));
    const currentFilteredProducts = filterCategory === 'All Products'
      ? products : products.filter(p => p.category === filterCategory);
    return { filteredProducts: currentFilteredProducts, categories: categoryList };
  }, [products, filterCategory]);

  const tabs = [
    { id: 'products', label: 'Products', icon: FiPackage },
    { id: 'testimonials', label: 'Testimonials', icon: FiMessageSquare },
    { id: 'content', label: 'Site Content', icon: FiLayout },
    { id: 'images', label: 'Media Library', icon: FiImage },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <AdminHeader title="CMS Dashboard" showBack={true} />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="bg-white p-2 rounded-xl shadow-sm border border-gray-200 mb-8 flex overflow-x-auto gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-all font-medium ${
                activeTab === tab.id
                  ? 'bg-green-800 text-white shadow-md'
                  : 'bg-transparent text-gray-600 hover:bg-gray-100'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* --- PRODUCTS TAB --- */}
        {activeTab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Product Inventory</h2>
              <button onClick={() => openModal('product')} className="bg-green-700 hover:bg-green-800 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 shadow-sm">
                <SafeIcon icon={FiPlus} className="w-5 h-5" /> Add Product
              </button>
            </div>
            
             <div className="flex gap-2 overflow-x-auto pb-4 mb-4">
               {categories.map((cat, idx) => (
                <button
                  key={idx}
                  onClick={() => setFilterCategory(cat.name)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    cat.name === filterCategory 
                    ? 'bg-green-800 text-white' 
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {cat.name} ({cat.count})
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product) => (
                <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group hover:shadow-md transition-all">
                  <div className="relative h-48 bg-gray-100 p-4">
                    <img src={product.image_url || "https://placehold.co/400x300"} alt={product.name} className="w-full h-full object-contain" />
                    <span className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold shadow-sm text-white ${product.status === 'Published' ? 'bg-green-500' : 'bg-gray-500'}`}>
                      {product.status}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-800 mb-1 truncate">{product.name}</h3>
                    <p className="text-xs text-gray-500 mb-4">{product.category}</p>
                    <div className="flex gap-2">
                      <button onClick={() => openModal('product', product)} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded text-sm font-medium">Edit</button>
                      <button onClick={() => handleDeleteItem(product.id, 'product')} className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 py-2 rounded text-sm font-medium">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* --- TESTIMONIALS TAB --- */}
        {activeTab === 'testimonials' && (
          <div>
             <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Customer Testimonials</h2>
              <button onClick={() => openModal('testimonial')} className="bg-green-700 hover:bg-green-800 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 shadow-sm">
                <SafeIcon icon={FiPlus} className="w-5 h-5" /> Add Testimonial
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-center gap-4 mb-4">
                    <img src={t.avatar_url || "https://placehold.co/100"} className="w-12 h-12 rounded-full object-cover" alt={t.name_en} />
                    <div>
                      <h4 className="font-bold text-gray-800">{t.name_en}</h4>
                      <p className="text-xs text-gray-500">{t.location_en}</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">"{t.text_en}"</p>
                  <div className="flex gap-2">
                    <button onClick={() => openModal('testimonial', t)} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded text-sm font-medium">Edit</button>
                    <button onClick={() => handleDeleteItem(t.id, 'testimonial')} className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 py-2 rounded text-sm font-medium">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* --- CONTENT TAB --- */}
        {activeTab === 'content' && (
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-1">Site Content</h2>
                <p className="text-gray-500 text-sm">Select a page to edit its text and images.</p>
              </div>
              
              <div className="flex gap-4 w-full md:w-auto">
                <select 
                  value={selectedPage}
                  onChange={(e) => setSelectedPage(e.target.value)}
                  className="px-4 py-2.5 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-green-500 outline-none cursor-pointer flex-1"
                >
                  {pages.map(page => (
                    <option key={page.id} value={page.id}>{page.name}</option>
                  ))}
                </select>
                
                <button 
                  onClick={saveContent}
                  disabled={saving}
                  className="bg-green-700 hover:bg-green-800 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 shadow-sm disabled:opacity-70 whitespace-nowrap"
                >
                  <SafeIcon icon={saving ? FiLoader : FiSave} className={`w-5 h-5 ${saving ? 'animate-spin' : ''}`} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-800 mx-auto"></div></div>
            ) : pageContent.length === 0 ? (
               <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
                 <p className="text-gray-500">No editable content found for this section yet.</p>
               </div>
            ) : (
              <div className="space-y-6">
                {pageContent.map((item) => (
                  <div key={item.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                      <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded font-bold uppercase tracking-wider">{item.section}</span>
                      <h3 className="font-bold text-gray-800 capitalize">{item.key.replace(/_/g, ' ')}</h3>
                    </div>

                    {item.type === 'image' ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="md:col-span-1">
                           <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                             <img src={item.content_en} alt="Preview" className="w-full h-full object-cover" />
                           </div>
                        </div>
                        <div className="md:col-span-2">
                           <ImageUpload 
                             onUploadComplete={(url) => handleContentChange(item.id, 'content_en', url)}
                           />
                           <input 
                             type="text"
                             value={item.content_en || ''}
                             onChange={(e) => handleContentChange(item.id, 'content_en', e.target.value)}
                             className="w-full mt-2 p-2 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded"
                             placeholder="Or paste Image URL"
                           />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-green-700 mb-1">English</label>
                          {item.type === 'long_text' ? (
                            <textarea 
                              value={item.content_en || ''}
                              onChange={(e) => handleContentChange(item.id, 'content_en', e.target.value)}
                              className="w-full p-4 rounded-lg border border-gray-300 focus:border-green-500 outline-none h-32"
                            />
                          ) : (
                            <input 
                              type="text"
                              value={item.content_en || ''}
                              onChange={(e) => handleContentChange(item.id, 'content_en', e.target.value)}
                              className="w-full p-3 rounded-lg border border-gray-300 focus:border-green-500 outline-none"
                            />
                          )}
                        </div>
                        
                        <div dir="rtl">
                          <label className="block text-sm font-medium text-amber-700 mb-1">Arabic (العربية)</label>
                          {item.type === 'long_text' ? (
                            <textarea 
                              value={item.content_ar || ''}
                              onChange={(e) => handleContentChange(item.id, 'content_ar', e.target.value)}
                              className="w-full p-4 rounded-lg border border-gray-300 focus:border-green-500 outline-none h-32"
                            />
                          ) : (
                            <input 
                              type="text"
                              value={item.content_ar || ''}
                              onChange={(e) => handleContentChange(item.id, 'content_ar', e.target.value)}
                              className="w-full p-3 rounded-lg border border-gray-300 focus:border-green-500 outline-none"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* --- IMAGES TAB --- */}
        {activeTab === 'images' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Media Library</h2>
              <div className="w-64">
                <ImageUpload onUploadComplete={fetchMediaFiles} />
              </div>
            </div>

            {mediaFiles.length === 0 ? (
               <div className="text-center py-20 text-gray-400">
                 No files found. Upload an image to get started.
               </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {mediaFiles.map((file, idx) => (
                  <div key={idx} className="bg-white p-2 rounded-lg shadow-sm border border-gray-200 group relative">
                    <div className="aspect-square bg-gray-100 rounded-md overflow-hidden mb-2">
                       <img src={file.publicUrl} alt={file.name} className="w-full h-full object-cover" />
                    </div>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(file.publicUrl);
                        alert("Copied URL!");
                      }}
                      className="w-full bg-gray-100 hover:bg-green-100 text-gray-600 hover:text-green-700 text-xs py-1 rounded flex items-center justify-center gap-1"
                    >
                      <SafeIcon icon={FiCopy} className="w-3 h-3" /> Copy URL
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* --- EDIT MODAL (Generic) --- */}
        <AnimatePresence>
          {isEditModalOpen && editingItem && (
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4 backdrop-blur-sm"
            >
              <motion.div 
                initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                  <h3 className="text-xl font-bold text-gray-800">
                    {editingItem.id ? 'Edit Item' : 'New Item'}
                  </h3>
                  <button onClick={() => setIsEditModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">
                    <SafeIcon icon={FiX} className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <form onSubmit={handleSaveItem} className="p-6 space-y-6">
                  {modalType === 'product' ? (
                    /* PRODUCT FORM */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="col-span-2">
                        <label className="label">Name</label>
                        <input className="input-field" value={editingItem.name} onChange={e => setEditingItem({...editingItem, name: e.target.value})} required />
                      </div>
                      <div>
                        <label className="label">Status</label>
                        <select className="input-field" value={editingItem.status} onChange={e => setEditingItem({...editingItem, status: e.target.value})}>
                           <option value="Draft">Draft</option><option value="Published">Published</option>
                        </select>
                      </div>
                      <div>
                        <label className="label">Category</label>
                        <select className="input-field" value={editingItem.category} onChange={e => setEditingItem({...editingItem, category: e.target.value})}>
                           <option value="Light Roast">Light Roast</option><option value="Medium Roast">Medium Roast</option><option value="Dark Roast">Dark Roast</option>
                        </select>
                      </div>
                      <div><label className="label">Roast Level</label><input className="input-field" value={editingItem.roast_level} onChange={e => setEditingItem({...editingItem, roast_level: e.target.value})} /></div>
                      <div><label className="label">Size</label><input className="input-field" value={editingItem.size} onChange={e => setEditingItem({...editingItem, size: e.target.value})} /></div>
                      <div className="col-span-2">
                        <label className="label">Image</label>
                        <ImageUpload currentImage={editingItem.image_url} onUploadComplete={(url) => setEditingItem({...editingItem, image_url: url})} />
                      </div>
                      <div className="col-span-2"><label className="label">Description (En)</label><textarea className="input-field h-24" value={editingItem.description} onChange={e => setEditingItem({...editingItem, description: e.target.value})} /></div>
                      <div className="col-span-2" dir="rtl"><label className="label">Description (Ar)</label><textarea className="input-field h-24" value={editingItem.arabic_description} onChange={e => setEditingItem({...editingItem, arabic_description: e.target.value})} /></div>
                    </div>
                  ) : (
                    /* TESTIMONIAL FORM */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div><label className="label">Name (En)</label><input className="input-field" value={editingItem.name_en} onChange={e => setEditingItem({...editingItem, name_en: e.target.value})} required /></div>
                      <div dir="rtl"><label className="label">Name (Ar)</label><input className="input-field" value={editingItem.name_ar} onChange={e => setEditingItem({...editingItem, name_ar: e.target.value})} /></div>
                      <div><label className="label">Location (En)</label><input className="input-field" value={editingItem.location_en} onChange={e => setEditingItem({...editingItem, location_en: e.target.value})} /></div>
                      <div dir="rtl"><label className="label">Location (Ar)</label><input className="input-field" value={editingItem.location_ar} onChange={e => setEditingItem({...editingItem, location_ar: e.target.value})} /></div>
                      <div className="col-span-2"><label className="label">Rating (1-5)</label><input type="number" min="1" max="5" className="input-field" value={editingItem.rating} onChange={e => setEditingItem({...editingItem, rating: parseInt(e.target.value)})} /></div>
                      <div className="col-span-2"><label className="label">Avatar</label><ImageUpload currentImage={editingItem.avatar_url} onUploadComplete={(url) => setEditingItem({...editingItem, avatar_url: url})} /></div>
                      <div className="col-span-2"><label className="label">Review (En)</label><textarea className="input-field h-24" value={editingItem.text_en} onChange={e => setEditingItem({...editingItem, text_en: e.target.value})} /></div>
                      <div className="col-span-2" dir="rtl"><label className="label">Review (Ar)</label><textarea className="input-field h-24" value={editingItem.text_ar} onChange={e => setEditingItem({...editingItem, text_ar: e.target.value})} /></div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
                    <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50">Cancel</button>
                    <button type="submit" disabled={saving} className="px-6 py-2.5 rounded-lg bg-green-700 text-white font-medium hover:bg-green-800 flex items-center gap-2">{saving && <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />} Save</button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      <style>{`.label { display: block; font-size: 0.875rem; font-weight: 500; color: #4b5563; margin-bottom: 0.5rem; } .input-field { width: 100%; padding: 0.75rem; border-radius: 0.5rem; border: 1px solid #d1d5db; outline: none; transition: border-color 0.2s; } .input-field:focus { border-color: #15803d; ring: 2px solid #bbf7d0; }`}</style>
    </div>
  );
};

export default WebsiteCustomizer;